/**
 * Telemetry must never gate the flow or surface to a member, so every delegation is
 * wrapped: a port member that throws synchronously, or returns a rejecting promise, is
 * logged and swallowed here rather than propagating into a screen's event handler.
 *
 * `void`-ing the result rather than awaiting it is deliberate — the async signatures below
 * are preserved only because callers already `await` some of them, not because anything
 * depends on the result.
 */
/**
 * Duck-typed rather than `instanceof Promise`, because the value comes from a **host's**
 * implementation and only has to be thenable, not a native promise. `instanceof` misses a
 * hand-rolled thenable, a Bluebird-style library promise, and any promise minted in another
 * realm (an iframe, a separate bundle's polyfill) — its rejection would then skip the
 * `.catch` below and surface as an unhandled rejection, which is the one thing this file
 * exists to prevent.
 */
const isThenable = (value: unknown): value is PromiseLike<void> =>
  typeof (value as PromiseLike<void> | null | undefined)?.then === 'function';

export const guard = (label: string, run: () => void | Promise<void>): void => {
  try {
    const result = run();
    // `Promise.resolve` rather than `result.catch`: a `PromiseLike` is only required to have
    // `then`, so `catch` may not exist on it. Resolving adopts the thenable and gives us a
    // native promise to attach to; a native promise passes straight through.
    if (isThenable(result)) {
      Promise.resolve(result).catch((error) => console.error(`Analytics: ${label} failed`, error));
    }
  } catch (error) {
    console.error(`Analytics: ${label} failed`, error);
  }
};
